
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { GAME_SETTINGS } from '@/utils/gameState';

interface HelpInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpInfoModal: React.FC<HelpInfoModalProps> = ({ isOpen, onClose }) => {
  const { wordLength, maxAttempts } = GAME_SETTINGS;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            Fourdle Guide
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="how-to-play" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="how-to-play">How to Play</TabsTrigger>
            <TabsTrigger value="game-tips">Game Tips</TabsTrigger>
          </TabsList>
          
          <TabsContent value="how-to-play" className="space-y-4 pt-4 text-sm sm:text-base">
            <p>
              Guess the Fourdle in {maxAttempts} tries.
            </p>
            
            <div>
              <p className="font-semibold mb-2">Game Rules:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Each guess must be a valid {wordLength}-letter word.</li>
                <li>The color of the tiles will change to show how close your guess was.</li>
              </ul>
            </div>
            
            <div>
              <p className="font-semibold mb-2">Examples:</p>
              
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-fourdle-correct text-white font-bold rounded">
                  W
                </div>
                <p>W is in the word and in the correct spot.</p>
              </div>
              
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center bg-fourdle-present text-white font-bold rounded">
                  I
                </div>
                <p>I is in the word but in the wrong spot.</p>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 flex items-center justify-center bg-fourdle-absent text-white font-bold rounded">
                  N
                </div>
                <p>N is not in the word.</p>
              </div>
            </div>
            
            <div>
              <p>A new Fourdle will be available each day in Daily mode!</p>
            </div>
          </TabsContent>
          
          <TabsContent value="game-tips" className="space-y-4 pt-4 text-sm sm:text-base">
            <div>
              <p className="font-semibold mb-2">Helpful Tips:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Try to use vowels in your first guess (A, E, I, O, U).</li>
                <li>Look for common consonants like R, S, T, N.</li>
                <li>Pay attention to the colors - they're your biggest clue!</li>
                <li>Use each guess strategically to eliminate or confirm letters.</li>
                <li>Remember that a letter can appear multiple times in a word.</li>
                <li>Use process of elimination - the keyboard shows which letters you've tried.</li>
                <li>Your game progress is saved, so you can come back later.</li>
                <li>Toggle between light and dark mode with the moon/sun icon.</li>
              </ul>
            </div>
            
            <div>
              <p className="font-semibold mb-2">Keyboard Shortcuts:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Type letters using your keyboard</li>
                <li>Press Enter to submit your guess</li>
                <li>Press Backspace to delete letters</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default HelpInfoModal;
